import { COLOR_FAMILY_LABELS, type ColorFamily } from '@repo/api-contracts';
import type { CSSProperties } from 'react';
import type { StoreProductsParams } from '@/entities/product';
import { COLOR_FAMILY_SWATCH_STYLES } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';

/** 걸린 드로어 조건 하나 — 칩 한 개로 그려지고, 누르면 next 조건으로 바꾼다. */
export interface ActiveFilterChip {
  readonly key: string;
  /** 칩에 보이는 글자 — 접근 이름은 "{label} 필터 해제". */
  readonly label: string;
  /** 색상 칩만 — 필터의 견본과 같은 대표색 점. */
  readonly swatchStyle?: CSSProperties;
  /** 이 조건 하나만 푼 결과 — 정렬·카테고리·기획전·다른 조건은 그대로 둔다. */
  readonly next: StoreProductsParams;
}

const withoutColor = (filters: StoreProductsParams, family: ColorFamily): StoreProductsParams => {
  const rest = (filters.colorFamilies ?? []).filter((value) => value !== family);
  return { ...filters, colorFamilies: rest.length > 0 ? rest : undefined };
};

/** 단가 범위 문구 — 한쪽 끝만 걸리면 "이상"·"이하"로 읽는다. */
const unitPriceLabel = (min: number | undefined, max: number | undefined): string => {
  if (min !== undefined && max !== undefined) {
    return `단가 ${formatCount(min)}~${formatKrw(max)}`;
  }

  return min !== undefined ? `단가 ${formatKrw(min)} 이상` : `단가 ${formatKrw(max ?? 0)} 이하`;
};

/**
 * 필터 드로어에서 거는 조건(색상·최소 주문 수량·단가)만 칩으로 편다 — 색상은 한 색에 칩 하나.
 * 카테고리(탭)·기획전(띠)·정렬(툴바)은 화면에 이미 제 자리가 있어 칩으로 겹쳐 두지 않는다.
 * 칩 수가 곧 툴바 "필터" 버튼의 개수 표식이다.
 */
export const toActiveFilterChips = (filters: StoreProductsParams): readonly ActiveFilterChip[] => {
  const colorChips = (filters.colorFamilies ?? []).map(
    (family): ActiveFilterChip => ({
      key: `color-${family}`,
      label: COLOR_FAMILY_LABELS[family],
      swatchStyle: COLOR_FAMILY_SWATCH_STYLES[family],
      next: withoutColor(filters, family),
    }),
  );

  const qtyChips: readonly ActiveFilterChip[] =
    filters.minOrderQtyMax === undefined
      ? []
      : [
          {
            key: 'min-order-qty',
            label: `최소 주문 ${formatCount(filters.minOrderQtyMax)}개 이하`,
            next: { ...filters, minOrderQtyMax: undefined },
          },
        ];

  const priceChips: readonly ActiveFilterChip[] =
    filters.unitPriceMin === undefined && filters.unitPriceMax === undefined
      ? []
      : [
          {
            key: 'unit-price',
            label: unitPriceLabel(filters.unitPriceMin, filters.unitPriceMax),
            next: { ...filters, unitPriceMin: undefined, unitPriceMax: undefined },
          },
        ];

  return [...colorChips, ...qtyChips, ...priceChips];
};

/** 드로어 조건만 모두 푼 결과 — 정렬·카테고리·기획전·페이지는 남긴다. */
export const clearDrawerFilters = (filters: StoreProductsParams): StoreProductsParams => ({
  ...filters,
  colorFamilies: undefined,
  minOrderQtyMax: undefined,
  unitPriceMin: undefined,
  unitPriceMax: undefined,
});
